import { useLocation, useNavigate } from 'react-router-dom'
import React, { useState, useEffect, useCallback } from 'react'
import OtpInput from 'react-otp-input'
import {
  confirmSignUp,
  forgotPassword,
  resendSignup,
  submitForgotPassword,
} from '../../services/AuthenticationService'
import { Container, Box, Button, Typography, Link } from '../shared/MaterialExports'
import CircularProgress from '@mui/material/CircularProgress';
import { MpTypography } from '../shared'
import AnalyticsManager from '../../services/AnalyticsManager'
import { useAppDispatch } from '../../app/hooks'
import { AuthLoader } from '../gateways/AuthLoader'
import { Auth } from '@aws-amplify/auth'
import { Toast } from '../toast/toast'
import { signInWithEmail } from '@meprism/shared/src/redux/authentication/authenticationSlice'
import useMediaQuery from '@mui/material/useMediaQuery'
import { theme } from '../../theme/OnboardingTheme'
import { useAuthContext } from '@meprism/shared/src/context/AuthContext'
import Loader from '@meprism/shared/src/components/atoms/Loader'

export type OtpAction =
  | 'SIGNUP_EMAIL'
  | 'CHANGE_PASSWORD'
  | 'SIGNIN_SOFTWARE_TOKEN_MFA'

export interface OtpRouteParam {
  username?: string
  password?: string
  actionType: OtpAction
  from?: Location
  destination?: string
}
interface CircularTimerProps {
  value: number;
  label: string;
}

const RESEND_COOLDOWN_KEY = 'otp_resend_started_at';
const RESEND_ATTEMPTS_KEY = 'otp_resend_attempts';
const RESEND_WINDOW_END_KEY = 'otp_resend_window_end';

const RESEND_COOLDOWN = 30; // seconds
const MAX_RESEND_ATTEMPTS = 3;
const RESEND_WINDOW_DURATION = 15 * 60; // 15 minutes

const CircularTimer = ({ value, label }: CircularTimerProps) => {
  return (
    <Box sx={{ position: 'relative', display: 'inline-flex', marginBottom: 2 }}>
      <CircularProgress
        variant="determinate"
        value={value}
        size={64}
        thickness={4}
        sx={{
          color: '#9135E0',
          backgroundColor: 'transparent',
          transition: 'all 1s linear',
        }}
      />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          sx={{
            fontSize: '12px',
            color: '#FFF',
            fontFamily: 'InterRegular',
          }}
        >
          {label}
        </Typography>
      </Box>
    </Box>
  );
};

const LoginOTP = () => {
  const { state } = useLocation()
  const from = (state as OtpRouteParam)?.from || '/'
  const [timer, setTimer] = useState<number>(() => {
    const ts = sessionStorage.getItem(RESEND_COOLDOWN_KEY);
    if (!ts){
      sessionStorage.setItem(RESEND_COOLDOWN_KEY, Date.now().toString());
      return RESEND_COOLDOWN;
    }

    const elapsed = Math.floor((Date.now() - Number(ts)) / 1000);
    
    // If cooldown has passed, reset timer
    if (elapsed >= RESEND_COOLDOWN) {
      const now = Date.now();
      sessionStorage.setItem(RESEND_COOLDOWN_KEY, now.toString());
      return RESEND_COOLDOWN;
    }
    return Math.max(RESEND_COOLDOWN - elapsed, 0);
  });

  const [isResendEnabled, setIsResendEnabled] = useState<boolean>(timer===0)
  const { username, password, actionType, destination } = state as OtpRouteParam
  const [otp, setOtp] = useState<string>('')
  const [errorText, setErrorText] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const authContext = useAuthContext()

  useEffect(()=> {
    if (timer === 0) {
      setIsResendEnabled(true)
    }
     // --- Start countdown interval for the cooldown timer ---
    const interval = setInterval(() => {
    setTimer((prev) => {
      if (prev <= 1) {
        setIsResendEnabled(true);
        return 0;
      }
      return prev - 1;
    });
  }, 1000);
  return () => clearInterval(interval);

  },[timer])

  useEffect(() => {
    const now = Date.now();
    const windowEnd = sessionStorage.getItem(RESEND_WINDOW_END_KEY);

    // If window expired → reset attempts and window end
    if (now > Number(windowEnd)) {
      sessionStorage.setItem(
        RESEND_WINDOW_END_KEY,
        (now + RESEND_WINDOW_DURATION * 1000).toString()
      );
      sessionStorage.setItem(RESEND_ATTEMPTS_KEY, '0');
    }
  }, []);

  const onVerify = useCallback(async() =>{
    setLoading(true)
    switch (actionType) {
      case 'SIGNUP_EMAIL':
        try {
          await confirmSignUp(username as string, otp)
          await dispatch(
            signInWithEmail({ username, password } as {
              username: string
              password: string
            }),
          ).unwrap()
          AnalyticsManager.trackTypedEvent({
            event: 'Otp Verified - Web',
            source: "Web App"
          })
          navigate(from)
          } 
        catch (error) {
          setLoading(false)
          if (error instanceof Error) {
            switch (error.name) {
              case 'CodeMismatchException':
                setErrorText(
                  'The code you have entered is incorrect. Please try again',
                )
                break
              case 'ExpiredCodeException':
                await resendSignup(username as string)
                setErrorText(
                  `The code you entered has expired. We have sent a new one to ${username}`,
                )
                break
              default:
                setErrorText(
                  'An error occurred processing your request. Please try again later.',
                )
                AnalyticsManager.error(
                  `Unhandled Authorization error: `,
                  error,
                )
                break
            }
          } else {
            AnalyticsManager.error(`Unhandled Authorization error: `, error)
          }
        }
        break
      case 'CHANGE_PASSWORD':
          try {
            await submitForgotPassword(
              username as string,
              otp as string,
              password as string,
            )
            await dispatch(
              signInWithEmail({ username, password } as {
                username: string
                password: string
              }),
            ).unwrap()
            await AnalyticsManager.registerSuperProperties(
              {"email": username}
            )
            await AnalyticsManager.trackTypedEvent(
              {
                event: 'Sign In - Web',
                source: "Web App"
              }
            )
            navigate('/changePasswordSuccess', { state: from })
          } 
          catch (error) {
            setLoading(false)
            if (error instanceof Error) {
              switch (error.name) {
                case 'CodeMismatchException':
                  setErrorText(
                    'The code you have entered is incorrect. Please try again',
                  )
                  break
                case 'ExpiredCodeException':
                  await forgotPassword(username as string)
                  setErrorText(
                    `The code you entered has expired. We have sent a new one to ${username}`,
                  )
                  break
                case 'LimitExceededException':
                  setErrorText(
                    `Unfortunately, you have exceeded a limit on the number of failed login attempts. Please wait 15 minutes and try again.`,
                  )
                  AnalyticsManager.error('CognitoLimit', error)
                  break
                default:
                  setErrorText(
                    'An error occurred processing your request. Please try again later.',
                  )
                  AnalyticsManager.error(
                    `Unhandled Authorization error: `,
                    error,
                  )
                  break
              }
            } else {
              AnalyticsManager.error(`Unhandled Authorization error: `, error)
            }
          }
        break
      case 'SIGNIN_SOFTWARE_TOKEN_MFA':
        try {
            await Auth.confirmSignIn(
              authContext.user,
              otp,
              'SOFTWARE_TOKEN_MFA',
            )
            navigate(state.from || '/', { replace: true })
          } catch (error) {
            setLoading(false)
            switch (error?.name) {
              case 'CodeMismatchException':
                setErrorText(
                  'The code you have entered is incorrect. Please try again',
                )
                break
              case 'ExpiredCodeException':
                navigate('/login', {
                  replace: true,
                  state: { from: state.from },
                })
                Toast.show({
                  type: 'error',
                  text1: 'Your code has expired. You must log in again',
                })
                break
              case 'LimitExceededException':
                setErrorText(
                  `Unfortunately, you have exceeded a limit on the number of failed login attempts. Please wait 15 minutes and try again.`,
                )
                AnalyticsManager.error('CognitoLimit', error)
                break
              default:
                setErrorText(
                  'An error occurred processing your request. Please try again later.',
                )
                AnalyticsManager.error(`Unhandled Authorization error: `, error)
                break
            }
          }
    }
  }, 
  [actionType,
  authContext.user,
  dispatch,
  from,
  navigate,
  otp,
  password,
  state,
  username])

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const handleResend = useCallback(async () => {
    const attempts = Number(sessionStorage.getItem(RESEND_ATTEMPTS_KEY) || '0');
    const windowEnd = Number(sessionStorage.getItem(RESEND_WINDOW_END_KEY));
    const now = Date.now();

    // Reset attempts if window has expired
    if (now > windowEnd) {
      sessionStorage.setItem(RESEND_ATTEMPTS_KEY, '0');
    }
    
    if (attempts >= MAX_RESEND_ATTEMPTS) {
      // setting expiry for the window if max attempts reached
      if (!windowEnd) {
        sessionStorage.setItem(
          RESEND_WINDOW_END_KEY,
          (now + RESEND_WINDOW_DURATION * 1000).toString()
        );
      }
      setErrorText(
        'You have reached the maximum number of resend attempts. Please try again later after 15 minutes.'
      );
      return;
    }

    try {
      await resendSignup(username as string)

      // Update resend timestamp
      sessionStorage.setItem(RESEND_COOLDOWN_KEY,now.toString());
      
      // Update resend attempts
      sessionStorage.setItem(RESEND_ATTEMPTS_KEY,(attempts + 1).toString());

      setIsResendEnabled(false)
      setTimer(RESEND_COOLDOWN);
      setErrorText('')
      Toast.show({
        type: 'success',
        text1: `A new code has been sent to ${username}`,
      })
    } catch (error) {
     if (error?.name === 'LimitExceededException') {
        setErrorText(
          `You have reached the maximum number of resend attempts. Please try again after 15 minutes.`
        );
      } else {
        setErrorText('Failed to resend code. Please try again later.');
      }
    }
  }, [username])

  return (
    <AuthLoader>
      <Container fixed maxWidth={'sm'}>
        <Box
          mt="30px"
          display="flex"
          flexDirection="column"
          alignItems="left"
          justifyContent="left">
          <Typography
            variant={'h2'}
            sx={{ marginTop: '50px', marginBottom: '10px' }}>
            Enter Account Verification Code
          </Typography>
          <MpTypography
            mt="40px"
            mb="40px"
            variant="subheader"
            textAlign="left">
            {`Enter the verification code sent to ${
              destination ?? username
            } in the field below.`}
          </MpTypography>
          <OtpInput
            value={otp}
            onChange={(value) => {
              const numericValue = value.replace(/\D/g, '');
              setOtp(numericValue);
            }}
            inputStyle={{
              width: isMobile ? '16.666%' : '25%',
              height: isMobile ? '42px' : '60px',
              border: '2px solid #EFEDFDB2',
              borderRadius: '10px',
              background: '#1A1B20',
              color: '#ffffff',
            }}
            numInputs={6}
            renderSeparator={<span>-</span>}
            renderInput={(props) =>
              (
              <input 
              {...props} 
              inputMode="numeric"
              pattern="[0-9]*"
              onBeforeInput={(event: React.FormEvent<HTMLInputElement>) => {
                const e = event.nativeEvent as InputEvent;
                if (e.data && !/^[0-9]$/.test(e.data)) {
                  event.preventDefault();
                }
              }}
              />
            )}
            onPaste={(e) => {
              e.preventDefault();
              const pastedText = e.clipboardData.getData('text');
              const digitsOnly = pastedText.replace(/\D/g, '');
              if (!digitsOnly) return;
              setOtp(digitsOnly.slice(0, 6));
            }}
          />
          {isMobile ? (
            <Box sx={{ minHeight: errorText ? 100 : 30 }}>
              <MpTypography color={'error'}>{errorText}</MpTypography>
            </Box>
          ) : (
            errorText && (
            <Box sx={{marginTop: 4}}>
              <MpTypography color={'error'}>{errorText}</MpTypography>
            </Box>
            )
          )}
          {/* Verify code button */}
          <Button 
            onClick={onVerify} 
            disabled={loading || otp.length < 6}
            sx={{ mt: 10, width: '100%' }}>
            <Box 
            sx={{
              display: 'flex',   
              alignItems: 'center',
              gap: 2,
              justifyContent: 'center',
              whiteSpace: 'nowrap',
              width: '20%',
              '& .MuiCircularProgress-root': {
                color: '#5856d6 !important',
                width: '20px !important',
                height: '20px !important',
              },
              }}
            >
            <Box sx={{ flexGrow: 1, textAlign: 'center' }}>
              Verify Code
            </Box>
            <Loader loading={loading} />
          </Box>
          </Button>
          <Box sx={{display: 'flex', alignItems: 'center', flexDirection: 'column', mt: 5}}>
            <CircularTimer
              value={(timer / RESEND_COOLDOWN) * 100}
              label={`00:${timer.toString().padStart(2, '0')}`}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2}}>
              <Typography 
              sx={{
                fontSize: '14px',
                color: '#FFF',
              }}
              >
                Didn&apos;t receive the code?
              </Typography>
              <Link
                  component="button"
                  onClick={handleResend}
                  disabled={!isResendEnabled}
                  sx={{ 
                    textDecoration: 'none', 
                    color: '#9135E0',
                    pointerEvents: isResendEnabled ? 'auto' : 'none',
                    fontSize: '14px !important',
                    fontFamily: 'InterSemiBold',
                    '&:hover': {
                      textDecoration: isResendEnabled ? 'underline' : 'none',
                    },
                  }}
                >
                  Click to Resend
              </Link>
            </Box>
          </Box>
        </Box>
      </Container>
    </AuthLoader>
  )
}

export default LoginOTP
